import { parseFormattedNumber } from '@/utils/parsers/numbers'
import { formatAmount } from '@vaultsfyi/common/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Input } from '../common/input'
import { Separator } from '../common/separator'
import { Slider } from '../common/slider'

interface UsdRangeSelectorProps {
  initialValue: [number, number]
  minValue?: number
  maxValue: number
  minPlaceholder?: string
  onChange: (value: [number, number]) => void
}

export const UsdRangeSelector = ({
  initialValue,
  minValue = 0,
  maxValue,
  minPlaceholder = 'Min',
  onChange,
}: UsdRangeSelectorProps) => {
  const bounds = useMemo(() => getRangeBounds(minValue, maxValue), [minValue, maxValue])
  const constrainedInitialValue = useMemo(
    () => constrainRange(initialValue, bounds.minValue, bounds.maxValue),
    [bounds.maxValue, bounds.minValue, initialValue]
  )

  const [displayValue, setDisplayValue] = useState(constrainedInitialValue)
  const [sliderRange, setSliderRange] = useState(
    constrainedInitialValue.map((value) => valueToSliderScale(value, bounds))
  )
  const [inputRange, setInputRange] = useState<[string, string]>(
    constrainedInitialValue.map((num) => formatAmount(num, { notation: 'compact' })) as [string, string]
  )
  const [minFocused, setMinFocused] = useState(false)
  const [maxFocused, setMaxFocused] = useState(false)
  const minInputRef = useRef<HTMLInputElement>(null)
  const maxInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setDisplayValue(constrainedInitialValue)
    setSliderRange(constrainedInitialValue.map((value) => valueToSliderScale(value, bounds)))
    setInputRange(constrainedInitialValue.map((num) => formatAmount(num, { notation: 'compact' })) as [string, string])
  }, [bounds, constrainedInitialValue])

  const handleInputRangeChange = useCallback(() => {
    const nextLowerBound = parseFormattedNumber(inputRange[0]) ?? displayValue[0]
    const nextUpperBound = parseFormattedNumber(inputRange[1]) ?? displayValue[1]
    const newValue = constrainRange([nextLowerBound, nextUpperBound], bounds.minValue, bounds.maxValue)

    setDisplayValue(newValue)
    setSliderRange(newValue.map((value) => valueToSliderScale(value, bounds)))
    onChange(newValue)
  }, [bounds, displayValue, inputRange, onChange])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (minFocused || maxFocused) {
        if (e.key === 'Enter' || e.key === 'Escape') {
          handleInputRangeChange()
          minInputRef.current?.blur()
          maxInputRef.current?.blur()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [minFocused, maxFocused, handleInputRangeChange])

  return (
    <div className="flex flex-col gap-1">
      <Slider
        min={SLIDER_MIN_VALUE}
        max={SLIDER_MAX_VALUE}
        value={sliderRange}
        onValueChange={(value) => {
          const usdValues = value.map((sliderValue) => sliderScaleToValue(sliderValue, bounds)) as [number, number]
          const constrainedValues = constrainRange(usdValues, bounds.minValue, bounds.maxValue)
          setDisplayValue(constrainedValues)
          setSliderRange(constrainedValues.map((usdValue) => valueToSliderScale(usdValue, bounds)))
          onChange(constrainedValues)
        }}
      />
      <div className="grid grid-cols-[1fr_0.5rem_1fr] items-center gap-1">
        <Input
          ref={minInputRef}
          value={minFocused ? inputRange[0] : formatAmount(displayValue[0], { notation: 'compact' })}
          onFocus={() => setMinFocused(true)}
          onBlur={() => {
            setMinFocused(false)
            handleInputRangeChange()
          }}
          onChange={(e) => setInputRange([e.target.value, inputRange[1]])}
          placeholder={minPlaceholder}
          inputMode="numeric"
        />
        <Separator />
        <Input
          ref={maxInputRef}
          value={maxFocused ? inputRange[1] : formatAmount(displayValue[1], { notation: 'compact' })}
          onFocus={() => setMaxFocused(true)}
          onBlur={() => {
            setMaxFocused(false)
            handleInputRangeChange()
          }}
          onChange={(e) => setInputRange([inputRange[0], e.target.value])}
          placeholder="Max"
          inputMode="numeric"
        />
      </div>
    </div>
  )
}

const SLIDER_MIN_VALUE = 0
const SLIDER_MAX_VALUE = 1000

interface RangeBounds {
  minValue: number
  maxValue: number
  logMinValue: number
  logMaxValue: number
}

const getRangeBounds = (minValue: number, maxValue: number): RangeBounds => {
  const safeMinValue = Number.isFinite(minValue) && minValue > 0 ? minValue : 0
  const safeMaxValue = Number.isFinite(maxValue) && maxValue > safeMinValue ? maxValue : safeMinValue + 1
  const positiveMinValue = Math.max(1, safeMinValue || 1)
  const positiveMaxValue = Math.max(positiveMinValue + 1, safeMaxValue)

  return {
    minValue: safeMinValue,
    maxValue: safeMaxValue,
    logMinValue: Math.log10(positiveMinValue),
    logMaxValue: Math.log10(positiveMaxValue),
  }
}

const constrainRange = (
  [lowerBound, upperBound]: [number, number],
  minValue: number,
  maxValue: number
): [number, number] => {
  const constrainedLowerBound = Math.min(Math.max(minValue, lowerBound), maxValue)
  const constrainedUpperBound = Math.min(Math.max(minValue, upperBound), maxValue)

  if (constrainedLowerBound > constrainedUpperBound) {
    return [constrainedUpperBound, constrainedUpperBound]
  }

  return [constrainedLowerBound, constrainedUpperBound]
}

const valueToSliderScale = (value: number, bounds: RangeBounds): number => {
  if (value <= bounds.minValue) {
    return SLIDER_MIN_VALUE
  }

  const normalizedToLogScale = normalize(Math.log10(value), bounds.logMinValue, bounds.logMaxValue)
  return denormalize(normalizedToLogScale, SLIDER_MIN_VALUE, SLIDER_MAX_VALUE)
}

const sliderScaleToValue = (sliderValue: number, bounds: RangeBounds): number => {
  if (sliderValue <= SLIDER_MIN_VALUE) {
    return bounds.minValue
  }

  const normalizedToLinearScale = normalize(sliderValue, SLIDER_MIN_VALUE, SLIDER_MAX_VALUE)
  return Math.round(10 ** denormalize(normalizedToLinearScale, bounds.logMinValue, bounds.logMaxValue))
}

const normalize = (value: number, min: number, max: number): number => (value - min) / (max - min)

const denormalize = (value: number, min: number, max: number): number => value * (max - min) + min
